import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Search, Filter, Plus, MapPin, Calendar, Briefcase, Users } from 'lucide-react';
import { fetchJobs, type JobFilters } from '@/lib/api';
import { Job, JOB_TYPE_LABELS, STATUS_LABELS, JobType, JobStatus } from '@/types';
import { capitalizeFirst } from '@/lib/utils';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const JOB_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'paused', label: 'Paused' },
  { value: 'filled', label: 'Filled' },
  { value: 'cancelled', label: 'Cancelled' },
];

const JOB_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// Inverted label colors per job type (colored text + border on dark tint).
const JOB_TYPE_BADGE_CLASSES: Record<JobType, string> = {
  full_time: 'border-blue-500/30 bg-blue-500/15 text-blue-400',
  part_time: 'border-purple-500/30 bg-purple-500/15 text-purple-400',
  contract: 'border-amber-500/30 bg-amber-500/15 text-amber-400',
  internship: 'border-cyan-500/30 bg-cyan-500/15 text-cyan-400',
};

function getStatusBadgeVariant(status: JobStatus): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'open':
      return 'success';
    case 'filled':
      return 'default';
    case 'paused':
      return 'warning';
    case 'cancelled':
      return 'destructive';
    case 'draft':
    default:
      return 'secondary';
  }
}

function getPriorityBadgeVariant(priority: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (priority) {
    case 'urgent':
      return 'destructive';
    case 'high':
      return 'warning';
    case 'medium':
      return 'default';
    case 'low':
    default:
      return 'secondary';
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatSalary(min: number | null, max: number | null, currency: string): string {
  if (!min && !max) return '-';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 0,
  });
  if (min && max) {
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  }
  if (min) {
    return `From ${formatter.format(min)}`;
  }
  return `Up to ${formatter.format(max!)}`;
}

export function JobsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [jobType, setJobType] = useState('all');
  const [priority, setPriority] = useState('all');
  const pageSize = 10;

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearch(value);
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const filters: JobFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    status: status !== 'all' ? status : undefined,
    jobType: jobType !== 'all' ? jobType : undefined,
    priority: priority !== 'all' ? priority : undefined,
    page,
    limit: pageSize,
  }), [debouncedSearch, status, jobType, priority, page]);

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', filters],
    queryFn: () => fetchJobs(filters),
  });

  const columns: ColumnDef<Job>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Job Title',
        cell: ({ row }) => {
          const job = row.original;
          return (
            <Link
              to={`/jobs/${job.id}`}
              className="hover:text-emerald-500"
            >
              <div>
                <div className="font-medium flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-text-tertiary" />
                  {job.title}
                </div>
                {job.company && (
                  <div className="text-sm text-text-tertiary">{job.company.name}</div>
                )}
              </div>
            </Link>
          );
        },
      },
      {
        accessorKey: 'location',
        header: 'Location',
        cell: ({ row }) => {
          const job = row.original;
          if (!job.location) return <span className="text-text-tertiary">-</span>;
          return (
            <div className="flex items-center gap-1 text-sm">
              <MapPin className="h-3.5 w-3.5 text-text-tertiary" />
              <span>{job.location}</span>
              {job.remoteType && (
                <Badge variant="outline" className="text-xs ml-1">
                  {job.remoteType}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'jobType',
        header: 'Type',
        cell: ({ row }) => {
          const jobType = row.original.jobType;
          if (!jobType) return <span className="text-text-tertiary">-</span>;
          return (
            <Badge
              variant="outline"
              className={`text-xs ${JOB_TYPE_BADGE_CLASSES[jobType as JobType] ?? ''}`}
            >
              {JOB_TYPE_LABELS[jobType as JobType] || jobType}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'salary',
        header: 'Salary',
        cell: ({ row }) => {
          const job = row.original;
          return (
            <span className="text-sm">
              {formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'applicationCount',
        header: 'Applications',
        cell: ({ row }) => {
          const count = row.original.applicationCount ?? 0;
          return (
            <div className="flex items-center gap-1 text-sm">
              <Users className="h-3.5 w-3.5 text-text-tertiary" />
              {count}
            </div>
          );
        },
      },
      {
        accessorKey: 'deadline',
        header: 'Deadline',
        cell: ({ row }) => {
          const deadline = row.original.deadline;
          if (!deadline) return <span className="text-text-tertiary">-</span>;
          const isExpired = new Date(deadline) < new Date();
          return (
            <div className={`flex items-center gap-1 text-sm ${isExpired ? 'text-red-600' : ''}`}>
              <Calendar className="h-3.5 w-3.5 text-text-tertiary" />
              {formatDate(deadline)}
            </div>
          );
        },
      },
      {
        accessorKey: 'priority',
        header: 'Priority',
        cell: ({ row }) => {
          const priority = row.original.priority;
          return (
            <Badge variant={getPriorityBadgeVariant(priority)}>
              {capitalizeFirst(priority)}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge variant={getStatusBadgeVariant(status)}>
              {STATUS_LABELS[status]}
            </Badge>
          );
        },
      },
    ],
    [],
  );

  const handleFilterChange = (
    filterType: 'status' | 'jobType' | 'priority',
    value: string,
  ) => {
    if (filterType === 'status') {
      setStatus(value);
    } else if (filterType === 'jobType') {
      setJobType(value);
    } else {
      setPriority(value);
    }
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Jobs</h1>
          <p className="text-text-secondary mt-1">
            Manage job listings and track applications
          </p>
        </div>
        <Button asChild>
          <Link to="/jobs/new">
            <Plus className="h-4 w-4 mr-2" />
            Add Job
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
            <Input
              placeholder="Search by title, description, or skills..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-tertiary" />
            </div>

            <Select
              value={status}
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {JOB_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={jobType}
              onValueChange={(value) => handleFilterChange('jobType', value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {JOB_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={priority}
              onValueChange={(value) => handleFilterChange('priority', value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
        <DataTable
          columns={columns}
          data={data?.data ?? []}
          page={page}
          pageSize={pageSize}
          total={data?.pagination.total ?? 0}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No jobs found. Try adjusting your filters or create a new job."
          getRowId={(row) => row.id}
        />
      </div>
    </div>
  );
}
